import React from 'react';
import { type StyleProp, type TextStyle } from 'react-native';
import Animated from 'react-native-reanimated';

import {
  useTypewriterAnimation,
  useTypewriterAnimationCharStyle,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders text with a typewriter animation, revealing one character at a time.
 * Each character is rendered as an `Animated.Text` with animated opacity.
 *
 * Designed to be nested inside a parent `Text` component that provides
 * container styling (font, color, etc.).
 *
 * @example
 * ```tsx
 * <Text style={{ fontSize: 32 }}>
 *   <TypewriterText text="Hello, world!" charDelayMs={50} />
 * </Text>
 * ```
 */
export const TypewriterText = (params: Props) => {
  const { text, charDelayMs, style } = params;

  const characters = text.split('');

  // ─────────────────────────────────────────────────────────────────────────────

  const animatedIndex = useTypewriterAnimation({ text, charDelayMs });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      {characters.map((char, index) => (
        <TypewriterChar
          key={index}
          char={char}
          index={index}
          animatedIndex={animatedIndex}
          style={style}
        />
      ))}
    </>
  );
};

// ─── Components ─────────────────────────────────────────────────────────────

const TypewriterChar = (params: TypewriterCharProps) => {
  const { char, index, animatedIndex, style } = params;

  const animatedStyle = useTypewriterAnimationCharStyle({
    index,
    animatedIndex,
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.Text style={[animatedStyle, style]} accessible={false}>
      {char}
    </Animated.Text>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  text: string;
  charDelayMs?: number;
  style?: StyleProp<TextStyle>;
};

type TypewriterCharProps = {
  char: string;
  index: number;
  animatedIndex: Animated.SharedValue<number>;
  style?: StyleProp<TextStyle>;
};
